import type { PageContextDescriptor } from "@repo/app-types/agents/pageContext";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type {
  PlaybookId,
  PlaybookTree,
} from "@repo/app-types/entities/playbook";
import type { SignalId } from "@repo/app-types/entities/signal";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type { ChatSuggestion } from "@repo/ui/chat/chatEmptyState";
import { getPlaybookConfigSuggestions } from "./copilotPlaybookConfigSuggestions.js";
import { getSignalConfigSuggestions } from "./copilotSignalConfigSuggestions.js";

/**
 * What the copilot does on each page the admin app grounds it to. Keyed by the
 * page-context descriptor so the three decisions a surface makes — what the
 * agent is told it is looking at, whether the panel is on screen, and which
 * starters the empty state offers — cannot drift apart: a new descriptor
 * variant without an entry here fails to typecheck.
 *
 * Routes that produce no descriptor fall back to `FALLBACK_COPILOT_SURFACE`.
 */
export interface CopilotSurface<
  Descriptor extends PageContextDescriptor = PageContextDescriptor,
> {
  /**
   * Open the docked panel when the user lands here, so the agent is on screen
   * by default while they configure something. Closing it sticks until they
   * next navigate to a page that opens it.
   */
  opensPanel: boolean;
  /** The ids the starters need resolved; the suggestions hook fetches only these. */
  selectIds?: (descriptor: Descriptor) => CopilotSuggestionIds;
  buildSuggestions: (data: CopilotSuggestionData) => ChatSuggestion[];
}

/** What a page's starters need fetched. */
export interface CopilotSuggestionIds {
  playbookId?: PlaybookId;
  teammateId?: TeammateId;
  teammateAgentId?: TeammateId;
  projectId?: string;
  criterionId?: string;
  signalId?: SignalId;
}

/**
 * The resolved names and flags the starters read. The suggestions hook fills
 * it from the ids the page's entry selects, so each surface's builder stays a
 * pure function and the registry holds no fetching.
 */
export interface CopilotSuggestionData {
  /** The viewed playbook's outcome, lowercased for use mid-sentence. */
  outcomeName: string | undefined;
  outcomeDefinition: OutcomeDefinition | undefined;
  playbookTree: PlaybookTree | undefined;
  isKnowledgeBaseEnabled: boolean;
  /** The teammate the page is scoped to, known before their name has loaded. */
  teammateId: TeammateId | undefined;
  /** Undefined until the teammate's name has loaded. */
  teammateName: string | undefined;
  /** Undefined until the criterion resolves in the playbook's tree. */
  criterionName: string | undefined;
  /** Falls back to "this agent" so the starters always read naturally. */
  agentName: string;
  /** Undefined on the new-signal editor, which has no saved signal yet. */
  signalId: SignalId | undefined;
  /** Whether the open project is a training rather than a recruiting one. */
  isTrainingProject: boolean;
}

/** Shown when no surface produces more specific suggestions. */
const fallbackSuggestions: ChatSuggestion[] = [
  {
    label: "Summarize playbook performance",
    prompt:
      "Summarize the team's playbook performance over the past two weeks. Highlight the strongest and weakest criteria with examples.",
  },
  {
    label: "Find coaching risks",
    prompt:
      "Which teammates show coaching risks based on their recent calls? Include specific patterns and transcript examples.",
  },
  {
    label: "Research a topic",
    prompt:
      "What are the most common themes customers bring up in recent calls? Summarize the top topics with examples.",
  },
];

/** Starters for the call open in the grading slideover. */
const customerInteractionSuggestions: ChatSuggestion[] = [
  {
    label: "Summarize this call",
    prompt:
      "Summarize the call I'm viewing: what the customer wanted, how it went, and how it ended. Quote the moments that decided it.",
  },
  {
    label: "Explain the grades",
    prompt:
      "For the call I'm viewing, explain the criteria that were missed and quote the transcript evidence behind each grade.",
  },
];

/** Starters for the knowledge base document editor, carried over from the page's retired assistant. */
const knowledgeBaseDocumentSuggestions: ChatSuggestion[] = [
  {
    label: "Summarize this article",
    prompt:
      "Summarize the knowledge base document I'm viewing, and tell me what a rep would struggle to find in it.",
  },
  {
    label: "Improve clarity and structure",
    prompt:
      "Review the knowledge base document I'm viewing for clarity and structure, and propose the edits that would help a rep most on a live call.",
  },
  {
    label: "Check recent adherence findings",
    prompt:
      "Have recent calls produced knowledge base adherence findings against the document I'm viewing? If so, show me whether the document or the expectation is what needs to change.",
  },
];

/** Starters for the calls list, where the subject is the customers, not a rep. */
const playbookCallsSuggestions: ChatSuggestion[] = [
  {
    label: "Find common objections",
    prompt:
      "What are the most common objections customers are raising in recent calls? Include specific examples and how reps are handling them.",
  },
  {
    label: "Review negative calls",
    prompt:
      "Which recent calls had negative customer sentiment? Summarize what went wrong and identify patterns.",
  },
  {
    label: "Compile positive quotes",
    prompt:
      "Compile positive customer quotes from recent calls. Include specific quotes that demonstrate what the team is doing well.",
  },
];

const trainingProjectSuggestions: ChatSuggestion[] = [
  {
    label: "Review this training",
    prompt:
      "Review this training's chapters and lessons for gaps and improvements, then propose the changes you recommend.",
  },
  {
    label: "Add a chapter",
    prompt:
      "Add a chapter to this training covering a topic the team struggles with on recent calls. Include practice lessons.",
  },
  {
    label: "Improve a role play",
    prompt:
      "Review this training's role-play lessons for realism, simulate the weakest one, and propose better instructions.",
  },
];

const signalPerformanceSuggestions: ChatSuggestion[] = [
  {
    label: "Summarize what this signal caught",
    prompt:
      "Summarize what the signal I'm viewing has matched recently: the patterns behind the matches and how they have trended. Quote representative calls.",
  },
];

const automationRunSuggestions: ChatSuggestion[] = [
  {
    label: "Explain this run",
    prompt:
      "Walk me through the automation run I'm viewing: what it did, what it produced, and anything that went wrong.",
  },
];

const automationArtifactSuggestions: ChatSuggestion[] = [
  {
    label: "Summarize this report",
    prompt:
      "Summarize the report I'm viewing and tell me what it says I should act on first.",
  },
  {
    label: "Dig into the evidence",
    prompt:
      "For the report I'm viewing, pull the calls behind its main claims so I can check the evidence myself.",
  },
];

const performanceReportSuggestions: ChatSuggestion[] = [
  {
    label: "Summarize this report",
    prompt:
      "Summarize the report I'm viewing: the headline numbers, what moved, and what it suggests we do next.",
  },
  {
    label: "Explain what's driving it",
    prompt:
      "For the report I'm viewing, explain what is driving the biggest changes. Quote calls that show the pattern.",
  },
];

function buildTeammateSuggestions({
  teammateName,
  outcomeName,
}: CopilotSuggestionData): ChatSuggestion[] {
  if (!teammateName) {
    return fallbackSuggestions;
  }

  const suggestions: ChatSuggestion[] = [
    {
      label: "Prepare coaching notes",
      prompt: `Prepare actionable coaching notes for ${teammateName} based on their calls from the past 2 weeks. Include specific strengths to reinforce and areas for improvement with examples and transcript quotes.`,
    },
    {
      label: "Call highlights",
      prompt: `Find call highlights and lowlights for ${teammateName} from the past few weeks. Include specific quotes that demonstrate both strong performance and coaching opportunities.`,
    },
  ];

  if (outcomeName) {
    suggestions.push({
      label: `Analyze their ${outcomeName} rate`,
      prompt: `Analyze why the ${outcomeName} rate for ${teammateName} is where it is. What specific behaviors or patterns in their calls are contributing to this? What could they do differently?`,
    });
  }

  return suggestions;
}

function buildPlaybookCriteriaSuggestions({
  outcomeName,
}: CopilotSuggestionData): ChatSuggestion[] {
  const suggestions: ChatSuggestion[] = [
    {
      label: "What are the biggest improvement opportunities?",
      prompt:
        "Which playbook criteria is the team struggling with most? Provide specific examples and recommendations for improvement.",
    },
  ];

  if (outcomeName) {
    suggestions.push({
      label: `What's impacting ${outcomeName} most?`,
      prompt: `Which playbook criteria have the biggest impact on ${outcomeName}? Analyze the correlation between criteria adherence and outcomes.`,
    });
  }

  return suggestions;
}

/**
 * Coaching notes on the criterion, for the teammate the drill-down is scoped
 * to or else for the team. A scoped teammate whose name has not loaded gets
 * the fallback, never the team prompt.
 */
function buildPlaybookCriterionSuggestions({
  criterionName,
  teammateId,
  teammateName,
}: CopilotSuggestionData): ChatSuggestion[] {
  if (!criterionName || (teammateId && !teammateName)) {
    return fallbackSuggestions;
  }

  return [
    {
      label: teammateName
        ? "Prepare coaching notes for this teammate"
        : "Prepare coaching notes for the team",
      prompt: `Prepare actionable coaching notes for ${teammateName ?? "the team"} on the "${criterionName}" criterion. Include specific examples from recent calls and recommendations for improvement.`,
    },
  ];
}

/**
 * The message the Instructions tab's Test button sends: a check of the saved
 * draft, with no candidate, whose row opens in the tab's Results panel.
 */
export function buildTestInstructionsPrompt(agentName: string): string {
  return `Test ${agentName}'s instruction sections: run the automated instruction checks on the saved draft as it is, then give me a brief takeaway of what failed.`;
}

function buildTeammateAgentSuggestions({
  agentName,
}: CopilotSuggestionData): ChatSuggestion[] {
  return [
    {
      label: "Improve the instructions",
      prompt: `Review ${agentName}'s instruction sections for gaps, conflicts and missing guardrails, then propose the changes you recommend.`,
    },
    {
      label: "Test the instructions",
      prompt: buildTestInstructionsPrompt(agentName),
    },
    {
      label: "Simulate a call",
      prompt: `Simulate a typical customer calling ${agentName} and tell me where the agent could do better.`,
    },
    {
      label: "Propose test cases",
      prompt: `Review ${agentName}'s configuration and propose test cases that cover its main call paths.`,
    },
  ];
}

/** Used by every route the descriptor does not cover. */
export const FALLBACK_COPILOT_SURFACE: CopilotSurface = {
  opensPanel: false,
  buildSuggestions: () => fallbackSuggestions,
};

export const COPILOT_SURFACES: {
  [Page in PageContextDescriptor["page"]]: CopilotSurface<
    Extract<PageContextDescriptor, { page: Page }>
  >;
} = {
  teammate: {
    opensPanel: false,
    selectIds: ({ playbookId, teammateId }) => ({ playbookId, teammateId }),
    buildSuggestions: buildTeammateSuggestions,
  },
  playbook: FALLBACK_COPILOT_SURFACE,
  playbookCalls: {
    opensPanel: false,
    buildSuggestions: () => playbookCallsSuggestions,
  },
  playbookCriteria: {
    opensPanel: false,
    selectIds: ({ playbookId }) => ({ playbookId }),
    buildSuggestions: buildPlaybookCriteriaSuggestions,
  },
  playbookCriterion: {
    opensPanel: false,
    selectIds: ({ playbookId, criterionId, teammateId }) => ({
      playbookId,
      criterionId,
      teammateId,
    }),
    buildSuggestions: buildPlaybookCriterionSuggestions,
  },
  signalPerformance: {
    opensPanel: false,
    buildSuggestions: () => signalPerformanceSuggestions,
  },
  performanceReport: {
    opensPanel: false,
    buildSuggestions: () => performanceReportSuggestions,
  },
  topics: FALLBACK_COPILOT_SURFACE,
  customerInteraction: {
    opensPanel: false,
    buildSuggestions: () => customerInteractionSuggestions,
  },
  playbookConfiguration: {
    opensPanel: true,
    selectIds: ({ playbookId }) => ({ playbookId }),
    buildSuggestions: ({
      playbookTree,
      outcomeDefinition,
      isKnowledgeBaseEnabled,
    }) =>
      getPlaybookConfigSuggestions({
        playbookTree,
        outcomeDefinition,
        isKnowledgeBaseEnabled,
      }),
  },
  signalConfiguration: {
    opensPanel: true,
    selectIds: ({ signalId }) => ({ signalId }),
    buildSuggestions: ({ signalId }) =>
      getSignalConfigSuggestions({ isNewSignal: !signalId }),
  },
  automationConfiguration: {
    opensPanel: true,
    buildSuggestions: () => fallbackSuggestions,
  },
  automationRun: {
    opensPanel: false,
    buildSuggestions: () => automationRunSuggestions,
  },
  // Reached straight from a notification email, so the user arrives with
  // nothing else on screen and the panel is worth opening for them.
  automationArtifact: {
    opensPanel: true,
    buildSuggestions: () => automationArtifactSuggestions,
  },
  projectConfiguration: {
    opensPanel: true,
    selectIds: ({ projectId }) => ({ projectId }),
    buildSuggestions: ({ isTrainingProject }) =>
      isTrainingProject ? trainingProjectSuggestions : fallbackSuggestions,
  },
  teammateAgent: {
    opensPanel: true,
    selectIds: ({ teammateAgentId }) => ({ teammateAgentId }),
    buildSuggestions: buildTeammateAgentSuggestions,
  },
  knowledgeBaseDocument: {
    opensPanel: true,
    buildSuggestions: () => knowledgeBaseDocumentSuggestions,
  },
};

/**
 * The surface for the page the user is on, or the fallback when ungrounded.
 * Widened to the full union: the lookup cannot keep the key tied to the
 * descriptor's variant, and every caller passes the descriptor the key came
 * from.
 */
export function resolveCopilotSurface(
  descriptor: PageContextDescriptor | undefined,
): CopilotSurface {
  return descriptor
    ? (COPILOT_SURFACES[descriptor.page] as CopilotSurface)
    : FALLBACK_COPILOT_SURFACE;
}

/** The ids the page's starters need resolved; none off a page without data needs. */
export function selectCopilotSuggestionIds(
  descriptor: PageContextDescriptor | undefined,
): CopilotSuggestionIds {
  return descriptor
    ? (resolveCopilotSurface(descriptor).selectIds?.(descriptor) ?? {})
    : {};
}
